<template>
  <div>
    <v-card class="card">
      <v-tabs v-model="tab" class="align-center" density="default" height="64">
        <v-tab ripple value="list-subscriptions">Subscriptions</v-tab>
        <v-spacer></v-spacer>
        <v-text-field
          class="align-self-right mt-0 pt-0"
          append-inner-icon="mdi-magnify"
          v-model="search"
          label="Search"
          :single-line="true"
          hide-details
          variant="outlined"
          density="compact"
          v-if="tab === 'list-subscriptions'"
        >
        </v-text-field>
      </v-tabs>

      <v-window v-if="tab === 'list-subscriptions'">
        <v-data-table
          :loading="loadingSubscriptions"
          v-if="subscriptions"
          :headers="headersSubscriptions"
          :items="groupedSubscriptions"
          :search="search"
          hover
          v-model:expanded="expanded"
          show-expand
          v-model:sort-by="sortBy"
        >
          <template v-slot:item.payerUser.email="{ item }">
            <div>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.email }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.payerUser.firstname="{ item }">
            <div>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.firstname }} {{
                    item.payerUser?.lastname
                  }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.subscriptionType="{ item }">
            <div class="d-flex align-center ga-2">
              <strong v-if="getSubscriptionType(item.payerUser?.stripeCustomerId) === 'Stripe'"
                      class="text-deep-purple-accent-3">Stripe</strong>
              <strong v-else class="text-amber-accent-4">Enterprise</strong>
              <v-btn density="compact" variant="plain" icon="mdi-information-outline"
                     @click="getStripeSubscriptionInfo(item)"
                     :loading="loadingGetStripeSubscriptionInfoUserId === item.id"
                     color="#0D66D0"
              >
              </v-btn>
            </div>
          </template>

          <template v-slot:item.updatedAt="{ item }">
            <div>
              {{ formatDate(item.updatedAt) }}
            </div>
          </template>

          <template v-slot:item.createdAt="{ item }">
            <div>
              {{ formatDate(item.createdAt) }}
            </div>
          </template>

          <template v-slot:expanded-row="{ item }">
            <td colspan="6">
              <div class="pa-5">
                <h4 class="mb-3">{{ item.payerUser.firstname }}'s subscription members</h4>

                <v-data-table
                  :headers="headersMembers"
                  :items="item.children"
                  :items-per-page="-1"
                  hover
                  hide-default-footer
                  class="elevation-1"
                >
                  <template v-slot:item.subscriptionType="{ item: childItem }">
                    <div class="d-flex align-center ga-2">
                      <div>
                        <strong v-if="getSubscriptionType(item.payerUser?.stripeCustomerId) === 'Stripe'"
                                class="text-deep-purple-accent-3">Stripe</strong>
                        <strong v-else class="text-amber-accent-4">Enterprise</strong>
                      </div>
                      <v-tooltip
                        location="top"
                        v-if="getSubscriptionType(item.payerUser?.stripeCustomerId) === 'Enterprise'"
                      >
                        <template v-slot:activator="{ props }">
                          <div
                            v-bind="props"
                            class="action-button cursor-pointer"
                            @click="removeEnterpriseMember(childItem.email)"
                          >
                            <v-icon size="small" color="red">mdi-delete</v-icon>
                          </div>
                        </template>
                        <div>
                          <p>Remove member</p>
                        </div>
                      </v-tooltip>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </td>
          </template>

          <template v-slot:no-data>
            <v-alert v-if="search?.length" color="error" icon="mdi-alert">
              Your search for "{{ search }}" found no results.
            </v-alert>
            <div v-else>
              No data available
            </div>
          </template>
        </v-data-table>
      </v-window>
    </v-card>

    <admin-subscription-info-dialog
      :is-visible="!!subscriptionInfo"
      :subscriptionInfo="subscriptionInfo"
      @close="subscriptionInfo = null"
    />
  </div>
</template>

<script>
import format from 'date-fns/format'
import AdminSubscriptionInfoDialog from '@/components/AdminSubscriptionInfoDialog.vue'
import { mapState } from 'vuex'

export default {
  components: { AdminSubscriptionInfoDialog },
  data: () => ({
    tab: 'list-subscriptions',
    sortBy: [{ key: 'createdAt', order: 'desc' }],
    dialog: [],
    search: '',
    expanded: [],
    headersSubscriptions: [
      {
        title: 'CreatedAt',
        key: 'createdAt',
        sortable: true
      },
      {
        title: 'UpdatedAt',
        key: 'updatedAt',
        sortable: true
      },
      {
        title: 'Payer user email',
        sortable: false,
        key: 'payerUser.email'
      },
      {
        title: 'Payer user name',
        sortable: false,
        key: 'payerUser.firstname'
      },
      {
        title: 'Subscription Type',
        key: 'subscriptionType',
        sortable: false
      },
      { title: '', key: 'data-table-expand' }
    ],
    headersMembers: [
      {
        title: 'Email',
        sortable: false,
        key: 'email',
        width: '50%'
      },
      {
        title: 'Subscription Type',
        key: 'subscriptionType',
        sortable: false,
        width: '50%'
      }
    ],
    subscriptionInfo: null,
    loadingGetStripeSubscriptionInfoUserId: null
  }),
  computed: {
    ...mapState({
      subscriptions: state => state.subscriptions.subscriptions,
      loadingSubscriptions: state => state.subscriptions.loadingSubscriptions
    }),
    groupedSubscriptions () {
      const grouped = this.subscriptions.reduce((acc, subscription) => {
        const payerId = subscription.payerUser.id

        if (!acc[payerId]) {
          acc[payerId] = {
            id: subscription.payerUser.id,
            payerUser: subscription.payerUser,
            createdAt: subscription.createdAt,
            updatedAt: subscription.updatedAt,
            children: []
          }
        }

        // Add child subscriptions if it's not the parent
        if (subscription.email !== subscription.payerUser.email) {
          acc[payerId].children.push({ email: subscription.email })
        }

        return acc
      }, {})

      return Object.values(grouped)
    }
  },
  methods: {
    formatDate (date) {
      return format(new Date(date), 'MMMM d, yyyy HH:mm')
    },
    getSubscriptionType (customerId) {
      if (customerId.startsWith('ecus_')) {
        return 'Enterprise'
      } else if (customerId.startsWith('cus_')) {
        return 'Stripe'
      }
      return null
    },
    async removeEnterpriseMember (email) {
      await this.$store.dispatch('enterpriseCustomers/removeEnterpriseMember', { email })
      this.$store.dispatch('subscriptions/listSubscriptions')
    },
    async getStripeSubscriptionInfo (user) {
      this.loadingGetStripeSubscriptionInfoUserId = user.id
      const response = await this.$store.dispatch('subscriptions/getStripeSubscriptionInfo', { userId: user.id })
      this.loadingGetStripeSubscriptionInfoUserId = null
      if (response.success) {
        this.subscriptionInfo = {
          user,
          info: response
        }
      } else {
        if (response.message === 'No active subscription found.') {
          this.subscriptionInfo = {
            user,
            info: {
              subscriptionDetails: {
                status: 'canceled'
              }
            }
          }
        } else {
          this.$store.commit('snackbar/showMessage', {
            content: response.message,
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
        }
      }
    }
  },
  mounted () {
    this.$store.dispatch('subscriptions/listSubscriptions')
  }
}
</script>

<style scoped>
.card {
  border-radius: 12px;
  background: #FCFCFC;
  border: 1px solid #FCFCFC;
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}
</style>
